/**
 * Helpers de formatação da UI (sem PII — RN-M11-001). Datas em pt-BR, bytes
 * legíveis, rótulo/estilo de status e situação de arquivo.
 */

import { SituacaoArquivo, StatusCargaApi } from '../services/cargas-types';
import { VisitaFicha } from '../services/consulta-types';

export function formatarData(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export function formatarBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const unidades = ['KB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i += 1;
  }
  return `${valor.toFixed(1)} ${unidades[i]}`;
}

export function rotuloStatus(status: StatusCargaApi): string {
  switch (status) {
    case 'aguardando_upload':
      return 'Aguardando upload';
    case 'processando':
      return 'Processando';
    case 'concluida':
      return 'Concluída';
    case 'falha':
      return 'Falha';
    default:
      return status;
  }
}

export function classeStatus(status: StatusCargaApi): string {
  switch (status) {
    case 'concluida':
      return 'pill pill-ok';
    case 'falha':
      return 'pill pill-err';
    case 'processando':
      return 'pill pill-dup';
    default:
      return 'pill pill-muted';
  }
}

const numeroPtBr = new Intl.NumberFormat('pt-BR');

/**
 * ADR-048 (design §1.1 e §10): as classes de evidência complementar da leitura v0.4 — a declaração inclusive — mais o
 * carimbo impresso no próprio termo e os dados da foto (EXIF), que aparecem nos achados.
 */
const ROTULO_EVIDENCIA_COMPLEMENTAR: Readonly<Record<string, string>> = {
  DECLARACAO_RESPONSABILIDADE: 'Declaração',
  TESTE_VELOCIDADE: 'Teste de velocidade',
  TESTE_FTP: 'Teste de velocidade (FTP)',
  TELA_SISTEMA: 'Tela do sistema',
  APP_GPS: 'App de GPS',
  FOTO_CARIMBO: 'Foto com carimbo',
  TELA_MODEM: 'Tela do modem',
  ETIQUETA_EQUIPAMENTO: 'Etiqueta do equipamento',
  CARTAO_CHIP: 'Cartão do chip',
  OFICIO_SECRETARIA: 'Ofício da Secretaria',
  FOTO_INSTALACAO: 'Foto da instalação',
  CARIMBO_TERMO: 'Carimbo do termo',
  EXIF: 'Dados da foto',
};

/** O tipo é uma classe de evidência complementar (ADR-048)? */
export function ehEvidenciaComplementar(tipo: string | null | undefined): boolean {
  return typeof tipo === 'string' && Object.prototype.hasOwnProperty.call(ROTULO_EVIDENCIA_COMPLEMENTAR, tipo);
}

/**
 * O tipo do arquivo em Pastas: a planilha de instalações da carga (ADR-040) com nome; o compactado (ADR-043) com
 * quantos arquivos saíram dele ("Compactado — 12 arquivos"; enquanto extrai, só "Compactado"); o resto como vem.
 */
/** O tipo do arquivo em Pastas em palavras (Requisito 6.1) — nunca o código interno. */
const TIPOS_DOCUMENTO: Readonly<Record<string, string>> = {
  ...ROTULO_EVIDENCIA_COMPLEMENTAR,
  TERMO_ADESAO: 'Termo de adesão',
  OUTRO: 'Outro documento',
  ILEGIVEL: 'Ilegível',
  VARIADOS: 'Documentos variados',
  AGUARDANDO_LEITURA: 'Aguardando leitura',
  FALHA_LEITURA: 'Falha na leitura',
  NAO_ABRE_FORMATO: 'Não abre — formato não reconhecido',
  NAO_ABRE_VAZIO: 'Não abre — arquivo vazio',
  NAO_ABRE_CORROMPIDO: 'Não abre — corrompido',
  NAO_ABRE_PROTEGIDO: 'Não abre — protegido por senha',
  PLANILHA: 'Planilha de instalações',
  // ADR-046: o tipo reconhecido da planilha.
  PLANILHA_CONTROLE: 'Planilha de controle',
  LISTA_DESIGNACAO: 'Lista de designação',
  RELATORIO_FOTOGRAFICO: 'Relatório Fotográfico',
  PLANILHA_NAO_RECONHECIDA: 'Planilha não reconhecida',
  PLANILHA_EM_ANALISE: 'Planilha — em análise',
};

export function rotuloTipoDocumento(tipo: string | null | undefined, extraidos?: number): string {
  if (tipo === 'COMPACTADO') {
    if (!extraidos) return 'Compactado';
    return `Compactado — ${numeroPtBr.format(extraidos)} ${extraidos === 1 ? 'arquivo' : 'arquivos'}`;
  }
  // ADR-046: o RF com quantas fotos saíram dele (na pasta de mesmo nome).
  if (tipo === 'RELATORIO_FOTOGRAFICO' && extraidos) {
    return `Relatório Fotográfico — ${numeroPtBr.format(extraidos)} ${extraidos === 1 ? 'foto' : 'fotos'}`;
  }
  if (tipo === null || tipo === undefined) return '—'; // API anterior
  return TIPOS_DOCUMENTO[tipo] ?? tipo;
}

export function rotuloSituacao(situacao: SituacaoArquivo): string {
  switch (situacao) {
    case 'novo':
      return 'Novo';
    case 'duplicado':
      return 'Duplicado';
    case 'invalido':
      return 'Fora da carga';
    case 'planilha':
      return 'Planilha — importada automaticamente';
    case 'sistema':
      return 'Arquivo de sistema (ignorado)';
    default:
      return situacao;
  }
}

export function classeSituacao(situacao: SituacaoArquivo): string {
  switch (situacao) {
    case 'novo':
      return 'pill pill-ok';
    case 'duplicado':
      return 'pill pill-dup';
    case 'invalido':
      return 'pill pill-err';
    case 'planilha':
      return 'pill pill-ok';
    default:
      return 'pill pill-muted';
  }
}

/**
 * "New Doc.pdf — página 3 de 10" numa visita de página de PDF/TIFF (ADR-033, Req 4 AC1);
 * null na imagem única (sem página).
 */
export function paginaDoArquivo(ficha: Pick<VisitaFicha, 'caminhoOriginal' | 'pagina' | 'paginasArquivo'>): string | null {
  if (typeof ficha.pagina !== 'number' || ficha.caminhoOriginal === null) {
    return null; // imagem única, ou visita sem imagem (ADR-038)
  }
  const nome = ficha.caminhoOriginal.split('/').pop() ?? ficha.caminhoOriginal;
  const total = typeof ficha.paginasArquivo === 'number' ? ` de ${ficha.paginasArquivo}` : '';
  return `${nome} — página ${ficha.pagina}${total}`;
}

/** Motivos técnicos das falhas de arquivo e de página (ADR-033, api-contracts M2) → texto da tela. */
const MOTIVOS_FALHA: Readonly<Record<string, string>> = {
  PDF_PROTEGIDO: 'PDF protegido por senha',
  PDF_CORROMPIDO: 'PDF corrompido',
  TIFF_CORROMPIDO: 'TIFF corrompido',
  SEM_PAGINAS: 'arquivo sem páginas',
  NENHUMA_PAGINA_PROCESSADA: 'nenhuma página pôde ser processada',
  ERRO_RENDERIZACAO: 'erro ao gerar a imagem da página',
  TEMPO_ESGOTADO: 'tempo esgotado',
  MEMORIA_ESGOTADA: 'memória insuficiente',
  ERRO_DERIVADO: 'erro ao preparar a imagem',
  // ADR-046 (Requisito 6): o documento (.pdf, .jpg…) cujo conteúdo o pré-processamento não reconheceu.
  CONTEUDO_NAO_RECONHECIDO: 'corrompido — o conteúdo não corresponde à extensão',
};

export function rotuloMotivoFalha(motivo: string | null): string {
  if (!motivo) {
    return 'motivo não informado';
  }
  return MOTIVOS_FALHA[motivo] ?? motivo;
}

/** Por que o compactado foi recusado (ADR-043, relatório da carga) → texto da tela. */
const MOTIVOS_COMPACTADO: Readonly<Record<string, string>> = {
  COMPACTADO_SENHA: 'protegido por senha',
  COMPACTADO_CORROMPIDO: 'corrompido — não abre',
  COMPACTADO_LIMITE: 'acima dos limites da extração (arquivos, tamanho ou compressão)',
  COMPACTADO_PROFUNDIDADE: 'compactado dentro de compactado além do limite de profundidade',
  FALHA_EXTRACAO: 'a extração falhou depois de várias tentativas',
};

export function rotuloMotivoCompactado(motivo: string): string {
  return MOTIVOS_COMPACTADO[motivo] ?? motivo;
}

/** Por que o arquivo não abre (ADR-043, relatório da carga) → texto da tela. */
const MOTIVOS_NAO_ABRE: Readonly<Record<string, string>> = {
  VAZIO: 'arquivo vazio (0 byte)',
  CORROMPIDO: 'corrompido — não abre',
  FORMATO_DESCONHECIDO: 'formato não reconhecido',
  PROTEGIDO: 'protegido por senha',
};

/** O motivo exato do M5 quando há (o PDF/TIFF — Requisito 6.3); senão o do "não abre". */
export function rotuloMotivoNaoAbre(motivo: string, detalhe?: string | null): string {
  if (detalhe) return rotuloMotivoFalha(detalhe);
  return MOTIVOS_NAO_ABRE[motivo] ?? motivo;
}

/**
 * A data da visita como a tela mostra (spec escolas-ordenacao-e-formatos, Req 2): `AAAA-MM-DD` → `DD/MM/AAAA`; o texto
 * que não é data nesse formato (o motor não normalizou) aparece como está. Só a exibição — o valor guardado e o da API
 * não mudam. Sem `Date`: o dia nunca muda pelo fuso.
 */
export function formatarDataVisita(valor: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : valor;
}
